import { useState, useEffect, useRef } from 'react';
import { ToastContainer } from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { settingsApi } from '../api/client';
import type {
  AppSettings,
  SenderAddress,
  ShippingOptions,
  NotificationSettings,
  LabelFormat,
  LabelSize,
  DeliveryConfirmation,
} from '../types';

// US States for dropdown
const US_STATES = [
  { value: '', label: 'Select State' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AK', label: 'Alaska' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'ME', label: 'Maine' },
  { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' },
  { value: 'VA', label: 'Virginia' },
  { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WY', label: 'Wyoming' },
  { value: 'DC', label: 'District of Columbia' },
];

const CANADIAN_PROVINCES = [
  { value: '', label: 'Select Province' },
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

// EasyPost delivery confirmation options
const DELIVERY_CONFIRMATION_OPTIONS: { value: DeliveryConfirmation | ''; label: string; description: string }[] = [
  { value: '', label: 'None', description: 'No signature required' },
  { value: 'NO_SIGNATURE', label: 'No Signature', description: 'Release package without signature' },
  { value: 'SIGNATURE', label: 'Signature', description: 'Requires signature from someone at delivery address' },
  { value: 'ADULT_SIGNATURE', label: 'Adult Signature', description: 'Requires signature from an adult (21+)' },
  { value: 'INDIRECT_SIGNATURE', label: 'Indirect Signature', description: 'Signature from address or neighbor (FedEx)' },
  { value: 'DIRECT', label: 'Direct Signature', description: 'Signature from someone at delivery address (FedEx)' },
  { value: 'SERVICE_DEFAULT', label: 'Service Default', description: 'Use carrier default based on service level' },
];

// EasyPost label format options
const LABEL_FORMAT_OPTIONS: { value: LabelFormat; label: string; description: string }[] = [
  { value: 'PNG', label: 'PNG', description: 'Standard image format (default)' },
  { value: 'PDF', label: 'PDF', description: 'Portable Document Format' },
  { value: 'ZPL', label: 'ZPL', description: 'Zebra Programming Language (thermal printers)' },
  { value: 'EPL2', label: 'EPL2', description: 'Eltron Programming Language (thermal printers)' },
];

// EasyPost label size options
const LABEL_SIZE_OPTIONS: { value: LabelSize; label: string; formats: LabelFormat[] }[] = [
  { value: '4x6', label: '4" x 6" (Standard)', formats: ['PNG', 'ZPL'] },
  { value: '8.5x11', label: '8.5" x 11" (Letter)', formats: ['PDF'] },
  { value: '4x5', label: '4" x 5"', formats: ['EPL2'] },
];

function normalizeLabelCompatibility(options: ShippingOptions): ShippingOptions {
  const selectedSize = LABEL_SIZE_OPTIONS.find((option) => option.value === options.label_size);
  const compatibleSize = LABEL_SIZE_OPTIONS.find((option) => option.formats.includes(options.label_format));
  const normalized = selectedSize?.formats.includes(options.label_format) || !compatibleSize
    ? options
    : { ...options, label_size: compatibleSize.value };
  const allowedCustomCodes = new Set(['', 'PO', 'DP', 'RMA']);
  return {
    ...normalized,
    print_custom_1_code: allowedCustomCodes.has(normalized.print_custom_1_code) ? normalized.print_custom_1_code : '',
    print_custom_2_code: allowedCustomCodes.has(normalized.print_custom_2_code) ? normalized.print_custom_2_code : '',
  };
}

// Print custom code options supported by the FedEx-only deployment
const PRINT_CUSTOM_CODES = [
  { value: '', label: 'None' },
  { value: 'PO', label: 'PO (Purchase Order)' },
  { value: 'DP', label: 'DP (Department)' },
  { value: 'RMA', label: 'RMA (Return Authorization)' },
];

// Default values
const defaultSenderAddress: SenderAddress = {
  name: 'Fifty 410',
  company: 'Fifty 410',
  street1: '14362 N Frank Lloyd Wright Blvd',
  street2: 'Suite 1000-327',
  city: 'Scottsdale',
  state: 'AZ',
  zip: '85260',
  country: 'US',
  phone: '(480) 624-2500',
  email: '',
};

const defaultShippingOptions: ShippingOptions = {
  label_format: 'PNG',
  label_size: '4x6',
  delivery_confirmation: null,
  insurance_enabled: false,
  default_insurance_amount: 0,
  saturday_delivery: false,
  hold_for_pickup: false,
  carbon_neutral: false,
  print_custom_1: '',
  print_custom_1_code: '',
  print_custom_2: '',
  print_custom_2_code: '',
};

const defaultNotifications: NotificationSettings = {
  email_notifications: true,
  send_tracking_emails: true,
  tracking_email_template: 'default',
};

function SettingsPage() {
  const { toasts, removeToast, success, error } = useToast();

  // Form state
  const [senderAddress, setSenderAddress] = useState<SenderAddress>(defaultSenderAddress);
  const [shippingOptions, setShippingOptions] = useState<ShippingOptions>(defaultShippingOptions);
  const [notifications, setNotifications] = useState<NotificationSettings>(defaultNotifications);

  // UI state
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState<'sender' | 'shipping' | 'notifications'>('sender');
  const loadRequestRef = useRef(0);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
    return () => {
      loadRequestRef.current += 1;
    };
  }, []);

  const loadSettings = async () => {
    const requestId = ++loadRequestRef.current;
    setLoading(true);
    setLoadError('');
    try {
      const settings = await settingsApi.get();
      if (requestId !== loadRequestRef.current) return;
      if (settings.sender_address) setSenderAddress(settings.sender_address);
      if (settings.shipping_options) setShippingOptions(normalizeLabelCompatibility(settings.shipping_options));
      if (settings.notifications) setNotifications(settings.notifications);
    } catch (err) {
      if (requestId !== loadRequestRef.current) return;
      const message = err instanceof Error ? err.message : 'Failed to load settings';
      setLoadError(message);
      error('Settings Unavailable', message);
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!senderAddress.name || !senderAddress.street1 || !senderAddress.city || !senderAddress.state || !senderAddress.zip) {
      error('Missing Required Fields', 'Please fill in all required sender address fields.');
      return;
    }

    if (senderAddress.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(senderAddress.email)) {
      error('Invalid Email', 'Enter a valid sender email address.');
      return;
    }

    if (shippingOptions.insurance_enabled && shippingOptions.default_insurance_amount <= 0) {
      error('Invalid Insurance Amount', 'Enter an insurance amount greater than zero.');
      setActiveSection('shipping');
      return;
    }

    setSaving(true);
    try {
      const settings: AppSettings = {
        sender_address: senderAddress,
        shipping_options: shippingOptions,
        notifications: notifications,
      };
      await settingsApi.update(settings);
      success('Settings Saved', 'Your settings have been updated successfully.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save settings';
      error('Save Failed', message);
    } finally {
      setSaving(false);
    }
  };

  const handleSenderChange = (field: keyof SenderAddress, value: string) => {
    setSenderAddress((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'country' && value !== prev.country ? { state: '' } : {}),
    }));
  };

  const handleShippingChange = <K extends keyof ShippingOptions>(field: K, value: ShippingOptions[K]) => {
    setShippingOptions((prev) => {
      const updated = { ...prev, [field]: value };

      // Auto-adjust label size when format changes
      if (field === 'label_format') {
        const format = value as LabelFormat;
        const currentSizeOption = LABEL_SIZE_OPTIONS.find((opt) => opt.value === prev.label_size);
        if (currentSizeOption && !currentSizeOption.formats.includes(format)) {
          // Find a compatible size for the new format
          const compatibleSize = LABEL_SIZE_OPTIONS.find((opt) => opt.formats.includes(format));
          if (compatibleSize) {
            updated.label_size = compatibleSize.value;
          }
        }
      }

      return updated;
    });
  };

  const handleNotificationChange = (field: keyof NotificationSettings, value: string | boolean) => {
    setNotifications((prev) => ({ ...prev, [field]: value }));
  };

  // Get available sizes for current format
  const availableSizes = LABEL_SIZE_OPTIONS.filter((opt) => opt.formats.includes(shippingOptions.label_format));
  const availableRegions = senderAddress.country === 'CA' ? CANADIAN_PROVINCES : US_STATES;

  if (loading) {
    return (
      <div className="settings-page">
        <div className="panel" style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div className="skeleton skeleton-heading" style={{ width: '200px' }} />
          <div className="skeleton skeleton-text" style={{ width: '100%', height: '300px', marginTop: '20px' }} />
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <>
        <ToastContainer toasts={toasts} onClose={removeToast} />
        <div className="settings-page">
          <div className="panel" style={{ maxWidth: '640px', margin: '0 auto', textAlign: 'center' }}>
            <h2 style={{ marginBottom: '12px' }}>Settings Could Not Be Loaded</h2>
            <p role="alert" style={{ color: 'var(--error-text)', marginBottom: '20px' }}>{loadError}</p>
            <p style={{ color: 'var(--aged-mahogany-light)', marginBottom: '20px' }}>
              Saving is disabled to prevent temporary defaults from overwriting your stored configuration.
            </p>
            <button type="button" className="btn btn-primary" onClick={loadSettings}>
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div className="settings-page">
        <div className="settings-header">
          <h2>Settings</h2>
          <p>Manage your sender address, shipping options, and notification preferences.</p>
        </div>

        {/* Section tabs */}
        <div className="settings-tabs">
          <button
            className={`settings-tab ${activeSection === 'sender' ? 'active' : ''}`}
            onClick={() => setActiveSection('sender')}
          >
            Sender Address
          </button>
          <button
            className={`settings-tab ${activeSection === 'shipping' ? 'active' : ''}`}
            onClick={() => setActiveSection('shipping')}
          >
            Shipping Options
          </button>
          <button
            className={`settings-tab ${activeSection === 'notifications' ? 'active' : ''}`}
            onClick={() => setActiveSection('notifications')}
          >
            Notifications
          </button>
        </div>

        <div className="settings-content">
          {/* Sender Address Section */}
          {activeSection === 'sender' && (
            <div className="form-section">
              <h2>Sender Address</h2>
              <p className="section-description">
                This address will be used as the default sender (from) address for all shipments.
                EasyPost validates addresses against carrier databases.
              </p>

              <div className="form-grid-settings">
                <div className="form-group">
                  <label htmlFor="sender-name">Name *</label>
                  <input
                    type="text"
                    id="sender-name"
                    value={senderAddress.name}
                    onChange={(e) => handleSenderChange('name', e.target.value)}
                    placeholder="Full name or business name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sender-company">Company</label>
                  <input
                    type="text"
                    id="sender-company"
                    value={senderAddress.company}
                    onChange={(e) => handleSenderChange('company', e.target.value)}
                    placeholder="Company name (optional)"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="sender-street1">Street Address *</label>
                  <input
                    type="text"
                    id="sender-street1"
                    value={senderAddress.street1}
                    onChange={(e) => handleSenderChange('street1', e.target.value)}
                    placeholder="Street address"
                  />
                </div>

                <div className="form-group full-width">
                  <label htmlFor="sender-street2">Address Line 2</label>
                  <input
                    type="text"
                    id="sender-street2"
                    value={senderAddress.street2}
                    onChange={(e) => handleSenderChange('street2', e.target.value)}
                    placeholder="Apt, suite, unit, etc. (optional)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sender-city">City *</label>
                  <input
                    type="text"
                    id="sender-city"
                    value={senderAddress.city}
                    onChange={(e) => handleSenderChange('city', e.target.value)}
                    placeholder="City"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sender-state">{senderAddress.country === 'CA' ? 'Province' : 'State'} *</label>
                  <select
                    id="sender-state"
                    value={senderAddress.state}
                    onChange={(e) => handleSenderChange('state', e.target.value)}
                  >
                    {availableRegions.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="sender-zip">{senderAddress.country === 'CA' ? 'Postal Code' : 'ZIP Code'} *</label>
                  <input
                    type="text"
                    id="sender-zip"
                    value={senderAddress.zip}
                    onChange={(e) => handleSenderChange('zip', e.target.value)}
                    placeholder="ZIP code"
                    maxLength={10}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sender-country">Country</label>
                  <select
                    id="sender-country"
                    value={senderAddress.country}
                    onChange={(e) => handleSenderChange('country', e.target.value)}
                  >
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="sender-phone">Phone</label>
                  <input
                    type="tel"
                    id="sender-phone"
                    value={senderAddress.phone}
                    onChange={(e) => handleSenderChange('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="sender-email">Email</label>
                  <input
                    type="email"
                    id="sender-email"
                    value={senderAddress.email}
                    onChange={(e) => handleSenderChange('email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Shipping Options Section */}
          {activeSection === 'shipping' && (
            <div className="form-section">
              <h2>Shipping Options</h2>
              <p className="section-description">
                Configure default shipping preferences. These options align with the EasyPost API.
              </p>

              {/* Label Settings */}
              <h3 className="settings-subsection-title">Label Settings</h3>
              <div className="form-grid-settings">
                <div className="form-group">
                  <label htmlFor="label-format">Label Format</label>
                  <select
                    id="label-format"
                    value={shippingOptions.label_format}
                    onChange={(e) => handleShippingChange('label_format', e.target.value as LabelFormat)}
                  >
                    {LABEL_FORMAT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} - {opt.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="label-size">Label Size</label>
                  <select
                    id="label-size"
                    value={shippingOptions.label_size}
                    onChange={(e) => handleShippingChange('label_size', e.target.value as LabelSize)}
                  >
                    {availableSizes.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                  <p className="field-hint">Available sizes depend on selected format.</p>
                </div>
              </div>

              {/* Delivery Options */}
              <h3 className="settings-subsection-title">Delivery Options</h3>
              <div className="form-grid-settings">
                <div className="form-group full-width">
                  <label htmlFor="delivery-confirmation">Delivery Confirmation</label>
                  <select
                    id="delivery-confirmation"
                    value={shippingOptions.delivery_confirmation || ''}
                    onChange={(e) =>
                      handleShippingChange(
                        'delivery_confirmation',
                        e.target.value ? (e.target.value as DeliveryConfirmation) : null
                      )
                    }
                  >
                    {DELIVERY_CONFIRMATION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label} - {opt.description}
                      </option>
                    ))}
                  </select>
                  <p className="field-hint">
                    Some options are carrier-specific. SERVICE_DEFAULT is recommended for international shipments.
                  </p>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shippingOptions.saturday_delivery}
                      onChange={(e) => handleShippingChange('saturday_delivery', e.target.checked)}
                    />
                    Enable Saturday Delivery
                  </label>
                  <p className="field-hint">Request Saturday delivery when available (may incur additional charges).</p>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shippingOptions.hold_for_pickup}
                      onChange={(e) => handleShippingChange('hold_for_pickup', e.target.checked)}
                    />
                    Hold for Pickup
                  </label>
                  <p className="field-hint">Hold package at carrier facility for recipient pickup.</p>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shippingOptions.carbon_neutral}
                      onChange={(e) => handleShippingChange('carbon_neutral', e.target.checked)}
                    />
                    Carbon Neutral Shipping
                  </label>
                  <p className="field-hint">Offset carbon emissions for shipments (where supported by carrier).</p>
                </div>
              </div>

              {/* Insurance */}
              <h3 className="settings-subsection-title">Insurance</h3>
              <div className="form-grid-settings">
                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shippingOptions.insurance_enabled}
                      onChange={(e) => handleShippingChange('insurance_enabled', e.target.checked)}
                    />
                    Enable Shipping Insurance
                  </label>
                  <p className="field-hint">
                    EasyPost insurance costs 1% of shipment value. Covers damage, loss, and theft.
                  </p>
                </div>

                {shippingOptions.insurance_enabled && (
                  <div className="form-group">
                    <label htmlFor="insurance-amount">Default Insurance Amount (USD)</label>
                    <input
                      type="number"
                      id="insurance-amount"
                      value={shippingOptions.default_insurance_amount}
                      onChange={(e) => handleShippingChange('default_insurance_amount', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    <p className="field-hint">Default value to insure shipments for (in USD).</p>
                  </div>
                )}
              </div>

              {/* Custom Print Fields */}
              <h3 className="settings-subsection-title">Custom Label Fields</h3>
              <p className="field-hint" style={{ marginBottom: '16px' }}>
                Add reference numbers, PO numbers, or other custom text to shipping labels.
              </p>
              <div className="form-grid-settings">
                <div className="form-group">
                  <label htmlFor="print-custom-1">Custom Field 1</label>
                  <input
                    type="text"
                    id="print-custom-1"
                    value={shippingOptions.print_custom_1}
                    onChange={(e) => handleShippingChange('print_custom_1', e.target.value)}
                    placeholder="e.g., PO-12345"
                    maxLength={50}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="print-custom-1-code">Field 1 Type</label>
                  <select
                    id="print-custom-1-code"
                    value={shippingOptions.print_custom_1_code}
                    onChange={(e) => handleShippingChange('print_custom_1_code', e.target.value)}
                  >
                    {PRINT_CUSTOM_CODES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="print-custom-2">Custom Field 2</label>
                  <input
                    type="text"
                    id="print-custom-2"
                    value={shippingOptions.print_custom_2}
                    onChange={(e) => handleShippingChange('print_custom_2', e.target.value)}
                    placeholder="e.g., Order #789"
                    maxLength={50}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="print-custom-2-code">Field 2 Type</label>
                  <select
                    id="print-custom-2-code"
                    value={shippingOptions.print_custom_2_code}
                    onChange={(e) => handleShippingChange('print_custom_2_code', e.target.value)}
                  >
                    {PRINT_CUSTOM_CODES.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <div className="form-section">
              <h2>Notifications</h2>
              <p className="section-description">
                Configure email notifications and tracking updates.
              </p>

              <div className="form-grid-settings">
                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifications.email_notifications}
                      onChange={(e) => handleNotificationChange('email_notifications', e.target.checked)}
                    />
                    Enable email notifications
                  </label>
                  <p className="field-hint">Receive email alerts for shipment updates and errors.</p>
                </div>

                <div className="form-group full-width">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={notifications.send_tracking_emails}
                      onChange={(e) => handleNotificationChange('send_tracking_emails', e.target.checked)}
                    />
                    Send tracking emails to recipients
                  </label>
                  <p className="field-hint">Automatically send tracking information to recipients after label purchase.</p>
                </div>

                <div className="form-group">
                  <label htmlFor="tracking-template">Tracking Email Template</label>
                  <select
                    id="tracking-template"
                    value={notifications.tracking_email_template}
                    onChange={(e) => handleNotificationChange('tracking_email_template', e.target.value)}
                  >
                    <option value="default">Default Template</option>
                    <option value="minimal">Minimal</option>
                    <option value="detailed">Detailed</option>
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Save Button */}
        <div className="settings-actions">
          <button
            className={`btn btn-primary btn-large ${saving ? 'loading' : ''}`}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </>
  );
}

export default SettingsPage;
